import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import {
  CategoryScale,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useUnit } from 'effector-react'
import { Line } from 'react-chartjs-2'

import { getOptions } from '@/dialogs/PPVTimelineDialog/utils.ts'
import { $isNSFWOn } from '@/models/account.ts'
import { setMessageIdToFetch } from '@/models/chattingUI/messages/messages.ts'
import { ChartPagination } from '@/modules/chatting/ui/chat-window/fan-info/ppv-stats/ppv-timeline/pagination.tsx'
import type { TimelineData } from '@/modules/chatting/ui/chat-window/fan-info/ppv-stats/ppv-timeline/types'
import { usePagination } from '@/modules/chatting/ui/chat-window/fan-info/ppv-stats/ppv-timeline/use-pagination.ts'
import { useIsDarkTheme } from '@/utils/useTheme.tsx'

interface Props {
  data: TimelineData[]
  onTimelineClose: () => void
}

export const TimelineChart = memo(({ data, onTimelineClose }: Props) => {
  const isNSFWOn = useUnit($isNSFWOn)
  const [chartContainerRef, setChartContainerRef] =
    useState<HTMLDivElement | null>(null)
  const isDark = useIsDarkTheme()
  const { pageCount, pagination, setPagination, paginatedData } =
    usePagination(data)

  useEffect(() => {
    if (chartContainerRef && chartContainerRef.scrollLeft === 0) {
      chartContainerRef.scrollLeft = chartContainerRef.scrollWidth
    }
  }, [chartContainerRef])

  const dataProp = useMemo(
    () => ({
      datasets: [
        {
          data: paginatedData.map((item) => ({
            x: item.createdAt,
            y: item.price,
          })),
          borderWidth: 3,
          borderColor: '#36A2EB',
        },
      ],
    }),
    [paginatedData],
  )

  const tickColor = isDark ? '#626678' : '#D8D9DD'

  const options = useMemo(
    () => getOptions(paginatedData, tickColor, !!isNSFWOn),
    [isNSFWOn, paginatedData, tickColor],
  )

  const isChartOverflowing = paginatedData.length > 10

  const chartContainerStyles = useMemo(
    () =>
      isChartOverflowing
        ? {
            width: 750 + (paginatedData.length - 10) * 150,
          }
        : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paginatedData],
  )

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (chartContainerRef && event.deltaY !== 0 && !event.shiftKey) {
        chartContainerRef.scrollLeft += event.deltaY
        event.preventDefault()
      }
    }

    if (chartContainerRef) {
      chartContainerRef.addEventListener('wheel', handleWheel, {
        passive: false,
      })
    }

    return () => {
      if (chartContainerRef) {
        chartContainerRef.removeEventListener('wheel', handleWheel)
      }
    }
  }, [chartContainerRef])

  const handleViewChatClick = useCallback(
    (event: Event, tooltip: HTMLDivElement) => {
      const messageId = (event.target as HTMLDivElement).getAttribute(
        'data-message-id',
      )

      if (messageId && !Number.isNaN(Number(messageId))) {
        setMessageIdToFetch({ messageIdToFetch: +messageId })
        onTimelineClose()
        tooltip.remove()
      }
    },
    [onTimelineClose],
  )

  useLayoutEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const target = mutation.target as HTMLDivElement

        if (target.id === 'chartjs-tooltip') {
          target
            .querySelector('.message-link')
            ?.addEventListener('click', (event) =>
              handleViewChatClick(event, target),
            )
        }
      })
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [handleViewChatClick])

  return (
    <div>
      <div
        className="width-[2400px] max-w-full overflow-x-scroll"
        ref={isChartOverflowing ? setChartContainerRef : undefined}
      >
        <div
          className="h-[550px]"
          style={chartContainerStyles}
        >
          <Line
            data={dataProp}
            options={options}
            plugins={[
              CategoryScale,
              LinearScale,
              PointElement,
              LineController,
              LineElement,
              Tooltip,
              ChartDataLabels,
            ]}
          />
        </div>
      </div>
      <ChartPagination
        data={data}
        pageCount={pageCount}
        pagination={pagination}
        setPagination={setPagination}
      />
    </div>
  )
})

TimelineChart.displayName = 'TimelineChart'
